import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Settings, LogOut, ChevronDown } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface ProfileMenuProps {
  nama: string
  role?: string
  avatarUrl?: string
}

function useDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return { open, setOpen, ref }
}

export default function ProfileMenu({ nama, role = 'Pelanggan', avatarUrl }: ProfileMenuProps) {
  const { open, setOpen, ref } = useDropdown()
  const navigate = useNavigate()

  async function handleLogout() {
    await supabase.auth.signOut()
    setOpen(false)
    navigate('/')
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors"
      >
        {/* Avatar */}
        {avatarUrl ? (
          <img src={avatarUrl} alt={nama} className="w-8 h-8 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold shrink-0">
            {nama.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Nama + Role */}
        <div className="text-left hidden sm:block">
          <p className="text-sm font-medium leading-tight max-w-24 truncate">{nama}</p>
          <p className="text-xs text-white/60 leading-tight">{role}</p>
        </div>

        <ChevronDown size={14} className={`transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
            {avatarUrl ? (
              <img src={avatarUrl} alt={nama} className="w-8 h-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 shrink-0">
                {nama.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{nama}</p>
              <p className="text-xs text-gray-400">{role}</p>
            </div>
          </div>

          {/* Menu items */}
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Settings size={15} className="text-gray-400" />
            Pengaturan
          </Link>
          <div className="border-t border-gray-100" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut size={15} />
            Keluar
          </button>
        </div>
      )}
    </div>
  )
}
